/*
=========================================================
* RingFoods Custom Features Section
* "Built Around You" - Custom feature requests at no extra cost
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

// @mui icons
import BuildIcon from "@mui/icons-material/Build";
import EmailIcon from "@mui/icons-material/Email";
import TimelineIcon from "@mui/icons-material/Timeline";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import HandshakeIcon from "@mui/icons-material/Handshake";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";

import { brandPrimary, accentSuccess } from "pages/RingFoodsHomepage/theme";

const steps = [
  {
    icon: EmailIcon,
    title: "Describe What You Need",
    description:
      "Send us an email describing the feature, report, or dashboard view you'd like. A rough idea is all it takes — we'll figure out the rest.",
  },
  {
    icon: TimelineIcon,
    title: "Added to Our Backlog",
    description:
      "Every request goes into our development backlog. We'll share estimated timelines and keep you updated as we work through it.",
  },
  {
    icon: RocketLaunchIcon,
    title: "Best-Effort Delivery",
    description:
      "We work through requests on a best-effort basis. When your feature is ready, it goes live automatically — no updates to install, no extra charges.",
  },
];

const highlights = [
  {
    icon: DashboardCustomizeIcon,
    title: "Custom Reports & Views",
    description: "Need a specific way to see your call data, reservation trends, or menu performance? Just ask.",
  },
  {
    icon: BuildIcon,
    title: "Workflow Tweaks",
    description: "Want your AI agent to handle calls differently or integrate with a tool you use? We'll make it work.",
  },
  {
    icon: HandshakeIcon,
    title: "No Extra Cost",
    description: "If the feature improves our platform, we build it into your plan at no additional charge.",
  },
];

function CustomFeatures() {
  return (
    <MKBox component="section" py={{ xs: 8, md: 10 }} id="custom-features">
      <Container>
        {/* Section Header */}
        <Grid container spacing={3} sx={{ mb: { xs: 5, md: 7 } }}>
          <Grid item xs={12} lg={8} mx="auto" textAlign="center">
            <MKTypography
              variant="overline"
              fontWeight={700}
              sx={{
                color: accentSuccess,
                letterSpacing: 2,
                fontSize: "0.75rem",
                mb: 1,
                display: "block",
              }}
            >
              Only at RingFoods
            </MKTypography>
            <MKTypography
              variant="h2"
              fontWeight="bold"
              color="primary"
              sx={{ mb: 2, fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.5rem" } }}
            >
              We Build Features You Ask For
            </MKTypography>
            <MKTypography
              variant="body1"
              color="text"
              sx={{ fontSize: { xs: "1rem", md: "1.125rem" }, maxWidth: 640, mx: "auto" }}
            >
              Most platforms give you what they have and call it a day. We add every customer
              request to our development backlog and work to deliver it &mdash; at no extra cost.
            </MKTypography>
          </Grid>
        </Grid>

        {/* How It Works - 3 Steps */}
        <Grid container spacing={4} sx={{ mb: { xs: 6, md: 8 } }}>
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <Grid item xs={12} md={4} key={index}>
                <MKBox
                  sx={{
                    textAlign: "center",
                    p: { xs: 3, md: 4 },
                    borderRadius: 2,
                    border: "1px solid #E5E5E5",
                    backgroundColor: "white",
                    height: "100%",
                    position: "relative",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  {/* Step Number */}
                  <MKTypography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      position: "absolute",
                      top: 12,
                      left: 16,
                      color: "#A3A3A3",
                      fontSize: "0.7rem",
                      letterSpacing: 1,
                    }}
                  >
                    STEP {index + 1}
                  </MKTypography>

                  <MKBox
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      backgroundColor: `${accentSuccess}12`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    <Icon sx={{ fontSize: 28, color: accentSuccess }} />
                  </MKBox>
                  <MKTypography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    {step.title}
                  </MKTypography>
                  <MKTypography
                    variant="body2"
                    color="text"
                    sx={{ fontSize: "0.9rem", lineHeight: 1.7 }}
                  >
                    {step.description}
                  </MKTypography>
                </MKBox>
              </Grid>
            );
          })}
        </Grid>

        {/* Highlight Cards */}
        <Grid container spacing={3} sx={{ mb: { xs: 5, md: 6 } }}>
          {highlights.map((item, index) => {
            const Icon = item.icon;
            return (
              <Grid item xs={12} md={4} key={index}>
                <MKBox
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 2,
                    backgroundColor: "#FAFAFA",
                    height: "100%",
                  }}
                >
                  <MKBox
                    sx={{
                      width: 44,
                      height: 44,
                      minWidth: 44,
                      borderRadius: "50%",
                      backgroundColor: `${brandPrimary}0A`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ fontSize: 22, color: brandPrimary }} />
                  </MKBox>
                  <MKBox>
                    <MKTypography variant="body1" fontWeight={700} sx={{ mb: 0.5 }}>
                      {item.title}
                    </MKTypography>
                    <MKTypography
                      variant="body2"
                      color="text"
                      sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
                    >
                      {item.description}
                    </MKTypography>
                  </MKBox>
                </MKBox>
              </Grid>
            );
          })}
        </Grid>

        {/* CTA */}
        <Grid container justifyContent="center">
          <Grid item xs={12} textAlign="center">
            <MKTypography
              variant="body2"
              color="text"
              sx={{ opacity: 0.8, mb: 2, maxWidth: 480, mx: "auto" }}
            >
              Have an idea that would make your restaurant run smoother?
              We&apos;d love to hear it.
            </MKTypography>
            <MKButton
              variant="contained"
              size="large"
              component="a"
              href="mailto:support@ringfoods.com?subject=Feature%20Request"
              startIcon={<EmailIcon />}
              sx={{
                backgroundColor: `${brandPrimary} !important`,
                color: "#FFFFFF !important",
                px: 4,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 700,
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: `${brandPrimary} !important`,
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Request a Feature
            </MKButton>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default CustomFeatures;
